<template>
  <el-drawer :visible="visible" title="开票申请详情" size="760px" append-to-body class="invoice-detail-drawer" @close="$emit('update:visible', false)">
    <div v-loading="loading" class="drawer-content">
      <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon>
        <el-button type="text" @click="load">重新加载</el-button>
      </el-alert>
      <template v-if="detail">
        <div class="summary-card">
          <div><div class="summary-title">{{ detail.tenantName || '-' }}</div><div class="muted">申请编号 {{ detail.id }} · {{ displayTime(detail.createTime) }}</div></div>
          <div class="summary-amount">{{ money(detail.invoiceAmt) }}<div><el-tag size="small" :type="statusTag(detail.status)">{{ statusLabel(detail.status) }}</el-tag></div></div>
        </div>
        <el-alert v-if="detail.errorMsg" :title="detail.errorMsg" type="warning" :closable="false" show-icon />
        <h3>开票信息</h3>
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="租户编号">{{ detail.tenantId || '-' }}</el-descriptions-item>
          <el-descriptions-item label="管理方">{{ detail.managerName || detail.managerId || '-' }}</el-descriptions-item>
          <el-descriptions-item label="费用类型">{{ subjectLabel(detail.suject) }}</el-descriptions-item>
          <el-descriptions-item label="开票方式">{{ billTypeLabel(detail.billType) }}</el-descriptions-item>
          <el-descriptions-item label="发票类型">{{ invoiceTypeLabel(detail.fplxdm) }}</el-descriptions-item>
          <el-descriptions-item label="抬头类型">{{ titleTypeLabel(detail.invoiceTitleType) }}</el-descriptions-item>
          <el-descriptions-item label="发票抬头" :span="2">{{ detail.enterpriseName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="税号" :span="2">{{ detail.enterpriseTaxID || '-' }}</el-descriptions-item>
          <el-descriptions-item label="单位地址" :span="2">{{ detail.address || '-' }}</el-descriptions-item>
          <el-descriptions-item label="单位电话">{{ detail.phone || '-' }}</el-descriptions-item>
          <el-descriptions-item label="接收手机">{{ detail.mobilePhone || '-' }}</el-descriptions-item>
          <el-descriptions-item label="开户银行">{{ detail.openingBank || '-' }}</el-descriptions-item>
          <el-descriptions-item label="银行账号">{{ detail.bankNumber || '-' }}</el-descriptions-item>
          <el-descriptions-item label="接收邮箱" :span="2">{{ detail.email || '-' }}</el-descriptions-item>
          <el-descriptions-item label="发票项目" :span="2">{{ detail.billFullName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="发票税率">{{ hasValue(detail.billTariff) ? detail.billTariff : '-' }}</el-descriptions-item>
          <el-descriptions-item label="账单金额">{{ money(detail.balance) }}</el-descriptions-item>
          <el-descriptions-item label="发票号码">{{ detail.fphm || '-' }}</el-descriptions-item>
          <el-descriptions-item label="发票代码">{{ detail.fpdm || '-' }}</el-descriptions-item>
          <el-descriptions-item label="发票性质">{{ recordTypeLabel(detail.type) }}</el-descriptions-item>
          <el-descriptions-item label="申请流水号">{{ detail.fpqqlsh || '-' }}</el-descriptions-item>
          <el-descriptions-item label="备注" :span="2">{{ detail.bz || '-' }}</el-descriptions-item>
        </el-descriptions>
        <h3>关联账单 <el-button v-if="billError" type="text" @click="load">重试</el-button></h3>
        <el-alert v-if="billError" :title="billError" type="error" :closable="false" />
        <el-table v-else :data="bills" border size="small" empty-text="暂无关联账单">
          <el-table-column prop="rid" label="账单 / 流水编号" min-width="130" />
          <el-table-column label="账单日期" min-width="110"><template slot-scope="{ row }">{{ displayTime(row.occurDate) }}</template></el-table-column>
          <el-table-column label="账单类型" min-width="110"><template slot-scope="{ row }">{{ settleWayLabel(row.settleWay) }}</template></el-table-column>
          <el-table-column label="账单金额" min-width="100"><template slot-scope="{ row }">{{ money(row.balance) }}</template></el-table-column>
          <el-table-column label="实付金额" min-width="100"><template slot-scope="{ row }">{{ money(row.realAmt) }}</template></el-table-column>
        </el-table>
        <h3>开票进度 <el-button v-if="progressError" type="text" @click="load">重试</el-button></h3>
        <el-alert v-if="progressError" :title="progressError" type="error" :closable="false" />
        <el-timeline v-else-if="progress.length" class="progress-list">
          <el-timeline-item v-for="(item, index) in progress" :key="index" :type="progressType(item.status)" :hollow="Number(item.status) === -1">
            <div class="progress-title">{{ item.progressPoint || '-' }} <el-tag v-if="Number(item.status) === 0" type="danger" size="mini">未通过</el-tag><span v-if="Number(item.status) === -1" class="muted">待处理</span></div>
            <div class="muted">{{ displayTime(item.operateTime) }} {{ item.userName || '' }}</div>
            <div v-if="item.refuseReason" class="refuse-reason">{{ item.refuseReason }}</div>
          </el-timeline-item>
        </el-timeline>
        <el-empty v-else description="暂无开票进度" :image-size="60" />
        <h3>发票文件</h3>
        <el-alert v-if="fileError" :title="fileError" type="error" :closable="false"><el-button type="text" @click="load">重试</el-button></el-alert>
        <div v-else-if="files.length" class="file-actions"><el-button v-for="file in files" :key="file.type" size="small" icon="el-icon-document" @click="openFile(file.url)">查看 / 下载 {{ file.type }}</el-button></div>
        <div v-else class="muted">{{ detail.status === 6 ? '申请已撤回，无可下载的发票文件' : [2, 4].includes(detail.status) ? '暂无可下载的发票文件' : '发票开具完成后可查看文件' }}</div>
      </template>
      <div class="drawer-footer">
        <el-button size="small" @click="$emit('update:visible', false)">关闭</el-button>
        <el-button v-if="!readOnly && detail && !error && canAudit(detail, managerId)" type="primary" size="small" :disabled="loading" @click="$emit('audit', detail)">审核处理</el-button>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import api from '@/api/tenantInvoice'
import { getApiErrorMessage } from '@/utils/apiError'
import { money, displayTime, hasValue, normalizeInvoice, canAudit, statusLabel, statusTag, subjectLabel, invoiceTypeLabel, titleTypeLabel, billTypeLabel, recordTypeLabel, settleWayLabel, billDetailsQuery, invoiceFiles, safeFileUrl } from '../tenantInvoiceModel'

export default {
  name: 'TenantInvoiceDetailDrawer',
  props: { visible: Boolean, readOnly: Boolean, record: { type: Object, default: () => ({}) }, managerId: { type: [String, Number], default: '' } },
  data () {
    return { loading: false, detail: null, bills: [], progress: [], files: [], error: '', billError: '', progressError: '', fileError: '', loadVersion: 0 }
  },
  watch: {
    visible (value) { if (value) this.load(); else this.loadVersion++ },
    record () { if (this.visible) this.load() }
  },
  beforeDestroy () { this.loadVersion++ },
  methods: {
    money,
    displayTime,
    hasValue,
    canAudit,
    statusLabel,
    statusTag,
    subjectLabel,
    invoiceTypeLabel,
    titleTypeLabel,
    billTypeLabel,
    recordTypeLabel,
    settleWayLabel,
    progressType (value) { return Number(value) === 0 ? 'danger' : Number(value) === 1 ? 'success' : 'info' },
    openFile (value) { const url = safeFileUrl(value); if (url) window.open(url, '_blank', 'noopener,noreferrer') },
    async load () {
      const version = ++this.loadVersion
      const current = () => version === this.loadVersion && this.visible
      this.loading = true
      this.detail = null
      this.bills = []; this.progress = []; this.files = []
      this.error = ''; this.billError = ''; this.progressError = ''; this.fileError = ''
      try {
        const { data } = await api.detail(this.record.id)
        if (!current()) return
        if (!data || !hasValue(data.status)) throw new Error('开票详情返回不完整，请重试')
        const detail = normalizeInvoice({ ...this.record, ...data })
        if (String(detail.managerId) !== String(this.managerId)) throw new Error('该申请不属于当前管理方')
        if (this.readOnly && String(detail.tenantId) !== String(this.record.tenantId)) throw new Error('该申请不属于当前租户')
        this.detail = detail
        const tasks = [
          (async () => {
            try {
              const query = billDetailsQuery(this.detail)
              if (!query) return
              const result = await api.bills(query)
              if (!Array.isArray(result.data)) throw new Error('关联账单返回格式异常')
              if (current()) this.bills = result.data
            } catch (e) { if (current()) this.billError = getApiErrorMessage(e, '关联账单加载失败') }
          })(),
          (async () => {
            try {
              const result = await api.progress(this.record.id)
              if (!Array.isArray(result.data)) throw new Error('开票进度返回格式异常')
              if (current()) this.progress = result.data
            } catch (e) { if (current()) this.progressError = getApiErrorMessage(e, '开票进度加载失败') }
          })(),
          (async () => {
            try {
              if (![2, 4].includes(this.detail.status)) return
              this.files = invoiceFiles(this.detail)
              if (!this.detail.fpqqlsh) return
              const result = await api.files(this.detail.fpqqlsh)
              if (current()) this.files = invoiceFiles({ ...this.detail, ...result.data })
            } catch (e) { if (current()) this.fileError = getApiErrorMessage(e, '发票文件加载失败') }
          })()
        ]
        await Promise.allSettled(tasks)
      } catch (e) { if (current()) this.error = getApiErrorMessage(e, '开票详情加载失败') } finally { if (current()) this.loading = false }
    }
  }
}
</script>

<style lang="scss" scoped>
.invoice-detail-drawer {
  ::v-deep .el-drawer { max-width: 95vw; }
  ::v-deep .el-drawer__header { margin: 0; padding: 20px; border-bottom: 1px solid #ebeef5; font-weight: 600; color: #303133; }
  ::v-deep .el-drawer__body { overflow: hidden; }
}
.drawer-content { height: 100%; overflow-y: auto; padding: 20px; box-sizing: border-box; }
.summary-card { display: flex; justify-content: space-between; align-items: center; padding: 20px; background: #f5f8fc; border-radius: 8px; margin-bottom: 20px; gap: 12px; }
.summary-title { font-size: 18px; font-weight: 600; margin-bottom: 8px; }
.summary-amount { text-align: right; font-size: 24px; color: #1677ff; font-weight: 600; }
.muted { font-size: 13px; color: #909399; line-height: 1.8; overflow-wrap: anywhere; }
h3 { font-size: 15px; margin: 24px 0 14px; color: #303133; }
::v-deep .el-descriptions-item__content { overflow-wrap: anywhere; }
.progress-list { padding: 6px 8px; }
.progress-title { font-weight: 500; margin-bottom: 5px; }
.refuse-reason { color: #f56c6c; line-height: 1.6; white-space: pre-wrap; overflow-wrap: anywhere; }
.file-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.drawer-footer { display: flex; justify-content: flex-end; gap: 8px; position: sticky; bottom: -20px; background: #fff; padding: 18px 0; margin-top: 24px; border-top: 1px solid #ebeef5; }
</style>
